import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";

const Uniform = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Uniform Standards" 
        description="Dress code and professional appearance requirements"
      >
        <section className="space-y-6">
          <p>
            Your appearance reflects BlueLux's brand and professionalism. As an independent contractor,
            adhering to uniform standards helps build customer trust and presents a consistent company image.
          </p>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Provided Uniforms</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium">Initial Issue (No Cost)</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>3 BlueLux branded polo shirts (short or long sleeve)</li>
                <li>2 BlueLux branded hats (baseball cap style)</li>
                <li>1 BlueLux lightweight jacket (for cooler weather)</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-medium">Replacement Policy</h3>
              <p className="text-muted-foreground">
                Worn or damaged uniforms replaced free once annually. Additional replacements available 
                at cost ($15 per polo, $10 per hat, $30 per jacket).
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Daily Uniform Requirements</h2>
          <div className="border border-border rounded-lg p-6 space-y-4">
            <div>
              <h3 className="text-lg font-medium">✓ Required</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Clean BlueLux branded shirt (tucked in preferred but not required)</li>
                <li>Long pants or knee-length shorts (no athletic shorts)</li>
                <li>Closed-toe shoes with slip-resistant soles</li>
                <li>BlueLux hat or visor (sun protection)</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-medium">✗ Not Permitted</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Tank tops, sleeveless shirts, or cut-offs</li>
                <li>Shorts above the knee</li>
                <li>Flip-flops, sandals, or open-toe shoes</li>
                <li>Torn, stained, or excessively worn clothing</li>
                <li>Clothing with offensive language or images</li>
                <li>Hats from other companies or sports teams</li>
              </ul>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Pants & Shorts Guidelines</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium">Acceptable Options</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Khaki, navy, black, or dark gray work pants/cargo pants</li>
                <li>Denim jeans in good condition (no rips or excessive fading)</li>
                <li>Work shorts (cargo or khaki style, at or below knee)</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-medium">Seasonal Considerations</h3>
              <p className="text-muted-foreground">
                Summer (May-September): Shorts acceptable. Long pants still recommended for working 
                around heavy brush or spiny plants.
              </p>
              <p className="text-muted-foreground">
                Winter (October-April): Long pants preferred. May wear additional layers under uniform 
                shirt in cold weather.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Footwear Requirements</h2>
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-4">
            <p className="font-semibold text-destructive mb-2">Safety First</p>
            <p className="text-muted-foreground">
              Proper footwear is a safety requirement, not just appearance. Wet pool decks are slippery!
            </p>
          </div>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground">
            <li>Closed-toe shoes or boots required (no exceptions)</li>
            <li>Slip-resistant soles strongly recommended</li>
            <li>Work boots or athletic shoes acceptable</li>
            <li>Shoes must be in good condition (not falling apart)</li>
            <li>Water shoes or aqua socks acceptable in summer</li>
          </ul>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Personal Grooming</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium">Hygiene Standards</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Shower and use deodorant daily</li>
                <li>Maintain clean, trimmed fingernails</li>
                <li>Hair clean and neatly groomed</li>
                <li>Facial hair neatly trimmed (if worn)</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-medium">Hair & Facial Hair</h3>
              <p className="text-muted-foreground">
                Hair should be clean and controlled (pulled back if long). Beards and mustaches are 
                acceptable but must be neat and well-maintained.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium">Jewelry & Accessories</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Minimal jewelry for safety (rings can catch on equipment)</li>
                <li>No dangling earrings or necklaces</li>
                <li>Watches and fitness trackers acceptable</li>
                <li>Sunglasses encouraged</li>
              </ul>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Special Weather Provisions</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium">Extreme Heat (95°F+)</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Shorts permitted regardless of season</li>
                <li>Moisture-wicking undershirts encouraged</li>
                <li>Extra sun protection (sunscreen, hat) required</li>
                <li>Frequent hydration breaks</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-medium">Rain</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>BlueLux rain jacket may be worn over uniform</li>
                <li>Personal rain gear acceptable if no branding conflicts</li>
                <li>Routes may be postponed in severe weather</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-medium">Cold Weather (Below 50°F)</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Long pants required</li>
                <li>Long-sleeve uniform shirt or jacket</li>
                <li>Additional layers under uniform acceptable</li>
                <li>BlueLux beanie may be worn instead of cap</li>
              </ul>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Uniform Care</h2>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground">
            <li>Wash uniforms regularly (recommend after each use)</li>
            <li>Follow care label instructions to prevent fading</li>
            <li>Inspect uniforms weekly for damage or excessive wear</li>
            <li>Iron or steam shirts if you'll be meeting customers face-to-face</li>
            <li>Replace damaged uniforms promptly</li>
          </ul>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Consequences for Non-Compliance</h2>
          <div className="space-y-2">
            <p className="text-muted-foreground">
              As an independent contractor, you're expected to maintain professional appearance standards. 
              Consistent failure to meet uniform requirements may result in:
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>First violation: Verbal reminder</li>
              <li>Second violation: Written warning and required correction</li>
              <li>Third violation: Temporary suspension from route assignments</li>
              <li>Continued violations: Contract termination</li>
            </ul>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default Uniform;